<template>
  <main class="view" :class="{ entering: !textReady }">
    <nav
      class="slidenavigation noselect"
      @touchstart.prevent="touchStart"
      @touchmove="touchMove"
      @touchend.prevent="touchEnd"
    >
      <a class="left button" @click="left"></a>
      <a class="right button" @click="right"></a>
    </nav>

    <div class="stage noselect">
      <Slideshow :slides="slides" :images="images" :current="current" :opacity="opacity" />
    </div>

    <h3 v-if="year && !hideYearsProject" class="pageyear">{{ year }}</h3>

    <!-- Project title: bottom left (mirrors home-title placement) -->
    <p v-if="isCurrentPage && title" class="pagetitle">{{ title }}</p>

    <!-- Slide counter: bottom right, only when multiple slides -->
    <p v-if="slides.length > 1" class="project-counter">
      {{ current + 1 }}&nbsp;&nbsp;/&nbsp;&nbsp;{{ slides.length }}
    </p>
  </main>
</template>

<script setup>
import { useStore } from '@/KQL.js'
import { clearTransitionCover, setTransitionCover, transitionCover } from '@/composables/useTransitionCover'
import { setBackRoute } from '@/composables/useBackRoute'
import { projectFading } from '@/composables/useProjectFading'

const router = useRouter();
const route = useRoute();
const kql = useStore();

// Transform blocks into { files, text } that Slideshow/Slide expects.
// image blocks → slide with resolved file objects + caption
// text blocks  → slide with HTML text and no images
const slides = computed(() => {
  const blocks = kql.page?.blocks || [];
  return blocks.map(block => {
    if (block.type === 'image') {
      return { files: block.files || [], text: block.content?.caption || '' };
    }
    if (block.type === 'text') {
      return { files: [], text: block.content?.text || '' };
    }
    return null;
  }).filter(Boolean);
});
const images = computed(() => kql.page?.images || []);
const title = computed(() => kql.page?.title || '');
// Only show data that belongs to this route — guards against stale kql.page
// from a previous route (e.g. Index still in the store while Project loads)
const isCurrentPage = computed(() => kql.page?.uid === route.params.project)
const year = computed(() => isCurrentPage.value ? kql.page?.content?.year || '' : '')
const last = computed(() => slides.value.length - 1);

const hideYearsProject = computed(() => kql.site?.content?.hideyearsproject === 'true')

const current = ref(0);
const startAtLast = ref(false)
watch(() => kql.page, () => {
  if (!isCurrentPage.value) return
  if (startAtLast.value) {
    current.value = last.value
    startAtLast.value = false
  } else {
    current.value = 0
  }
})

// Text hidden while the entry animation plays; shows once the overlay clears.
// Also hidden when projectFading is set (Back clicked — text fades out before navigation).
const textReady = ref(!transitionCover.value)
watch(transitionCover, (val) => {
  if (!val) textReady.value = true
  else textReady.value = false
})
watch(projectFading, val => { if (val) textReady.value = false })
onActivated(() => {
  textReady.value = !transitionCover.value
  if (startAtLast.value && isCurrentPage.value) {
    current.value = last.value
    startAtLast.value = false
  }
})

// Preload all slide images as soon as slides are available
watch(slides, (newSlides) => {
  newSlides.forEach(slide => {
    (slide.files || []).forEach(f => {
      if (f.url) { const img = new Image(); img.src = f.url }
    })
  })
}, { immediate: true })

// Clear the index→project transition overlay once the correct page data has arrived
// AND the first slide image has painted. Guarding with isCurrentPage prevents a false
// "slides is empty" clear when kql.page is still stale from the previous route.
// Double-rAF gives the browser two frames to composite the slide image before the
// overlay begins its fade, eliminating the blank-frame glitch.
const clearOverlayWhenReady = () => {
  if (!isCurrentPage.value) return // stale page data — wait for the right fetch
  const firstUrl = slides.value[0]?.files?.[0]?.url
  if (!firstUrl) { clearTransitionCover(); return }
  const img = new Image()
  const done = () => requestAnimationFrame(() => requestAnimationFrame(() => clearTransitionCover()))
  img.onload = done
  img.onerror = done
  img.src = firstUrl
  // Safety valve: clear after 1200 ms even if the image never fires
  setTimeout(clearTransitionCover, 1200)
}
// Fire when slides change (data arrived) or when the page UID updates to ours
watch(slides, clearOverlayWhenReady, { immediate: true })
watch(isCurrentPage, clearOverlayWhenReady)
// keep-alive re-activation: the watches above don't re-fire if slides and
// isCurrentPage are unchanged (e.g. returning to a cached project whose data
// is already in the store). Call directly so the overlay always clears.
onActivated(clearOverlayWhenReady)

// Ordered list of project UIDs from the index, matching the archive order
const projectChildren = computed(() => {
  const pages = Object.values(kql.pages || {});
  const index = pages.find(p => p.uid === 'index');
  return Object.values(index?.children || {});
});
const projectUIDs = computed(() => projectChildren.value.map(c => c.uid));
const currentProjectIdx = computed(() => projectUIDs.value.indexOf(route.params.project));

// Navigate to another project. Use the current slide image as the overlay so
// the old slide doesn't flash while the new project's data loads.
const navigateToProject = (uid, goingBack = false) => {
  setBackRoute('/index');
  const currentFile = slides.value[current.value]?.files?.[0]
  if (currentFile?.url) setTransitionCover(currentFile)
  if (goingBack) startAtLast.value = true
  router.push('/index/' + uid);
};

// Left/right navigation: advance slides within the project; at the boundary
// move to the adjacent project. Back button is the only way back to /index.
const right = () => {
  if (current.value < last.value) {
    current.value += 1;
  } else {
    const nextIdx = currentProjectIdx.value + 1;
    if (nextIdx < projectUIDs.value.length) {
      navigateToProject(projectUIDs.value[nextIdx]);
    }
    // At the very last slide of the last project: do nothing
  }
};

const left = () => {
  if (current.value > 0) {
    current.value -= 1;
  } else {
    const prevIdx = currentProjectIdx.value - 1;
    if (prevIdx >= 0) {
      navigateToProject(projectUIDs.value[prevIdx], true);
    }
    // At the very first slide of the first project: do nothing
  }
};

//Swipe and tap navigation
const opacity = ref(1);
let touch = {};
let scale = {};

const touchStart = (event) => {
  const pageX = event.touches[0].pageX;
  touch = { start: pageX, screen: window.screen.width, movement: 0, goingforward: false };
  scale = { left: pageX, right: window.screen.width - pageX };
};

const touchMove = (event) => {
  const pageX = event.touches[0].pageX;
  touch.goingforward = pageX < touch.start;
  touch.movement = touch.goingforward ? touch.start - pageX : pageX - touch.start;
  const progress = touch.goingforward ? touch.movement / scale.left : touch.movement / scale.right;
  opacity.value = 1 - progress;
};

const touchEnd = (event) => {
  const pageX = event.changedTouches[0].pageX;
  if (touch.goingforward && opacity.value < 0.75) {
    right();
  } else if (touch.movement && opacity.value < 0.75) {
    left();
  }
  if (!touch.movement && pageX / touch.screen >= 0.5) {
    right();
  } else if (!touch.movement && pageX / touch.screen < 0.5) {
    left();
  }
  opacity.value = 1;
};
</script>
